import moment from 'moment';
import axios from '../../utils/axios';
import { VOPAY_ACCOUNT_STATUS, VOPAY_PAYMENT_TYPE } from '../../helper/constants';

/** Dispatch on window after create/cancel payment so Payments page refetches VoPay history. */
export const PAYMENTS_REFETCH_EVENT = 'grata:payments-refetch';

/** Dispatch after building bank setup so Payments page re-checks building onboarding status. */
export const PAYMENTS_BUILDING_ONBOARDING_RECHECK_EVENT = 'grata:payments-building-onboarding-recheck';

/** RFC3339 Zulu / UTC (e.g. 2026-06-01T04:00:00Z). Bracketed Z is literal — moment's Z token is offset, not UTC. */
const PAYMENTS_RFC3339_ZULU_FORMAT = 'YYYY-MM-DD[T]HH:mm:ss[Z]';

export function dispatchPaymentsPageRefetch() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PAYMENTS_REFETCH_EVENT));
  }
}

export function dispatchPaymentsBuildingOnboardingRecheck() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PAYMENTS_BUILDING_ONBOARDING_RECHECK_EVENT));
  }
}

export function normalizeVopayAccountStatus(status) {
  return String(status || '').trim().toLowerCase();
}

/** Org VoPay uses raw webhook/API statuses only (e.g. active, approved) — not building-derived statuses. */
export function isOrgVopayOnboardingComplete(status) {
  const normalizedStatus = normalizeVopayAccountStatus(status);
  return (
    normalizedStatus === VOPAY_ACCOUNT_STATUS.APPROVED ||
    normalizedStatus === VOPAY_ACCOUNT_STATUS.NOT_FOUND ||
    normalizedStatus === VOPAY_ACCOUNT_STATUS.ACTIVE
  );
}

export function isOrgVopayActive(status) {
  return normalizeVopayAccountStatus(status) === VOPAY_ACCOUNT_STATUS.ACTIVE;
}

/** VoPay onboarding URL from API (string or `{ url }` object). */
export function resolveVopayOnboardingLink(link) {
  if (link == null) return '';
  if (typeof link === 'string') return link.trim();
  if (typeof link === 'object') {
    const url = link.url ?? link.URL ?? link.href ?? link.link;
    return typeof url === 'string' ? url.trim() : '';
  }
  return String(link).trim();
}

/** Rule 1: org application pending with an onboarding link — external Set up redirect. */
export function isOrgApplicationPendingWithLink(status, link) {
  return (
    normalizeVopayAccountStatus(status) === VOPAY_ACCOUNT_STATUS.APPLICATION_PENDING &&
    resolveVopayOnboardingLink(link) !== ''
  );
}

export function isOrgApplicationPendingWithoutLink(status, link) {
  return (
    normalizeVopayAccountStatus(status) === VOPAY_ACCOUNT_STATUS.APPLICATION_PENDING &&
    resolveVopayOnboardingLink(link) === ''
  );
}

export function isBuildingVopayReadyForPayments(status) {
  const normalizedStatus = normalizeVopayAccountStatus(status);
  return (
    normalizedStatus === VOPAY_ACCOUNT_STATUS.ACTIVE_AND_PAYMENT_METHODS_SYNCED ||
    normalizedStatus === VOPAY_ACCOUNT_STATUS.ACTIVE_BUT_GRATA_BANK_FAILED ||
    normalizedStatus === VOPAY_ACCOUNT_STATUS.ACTIVE_BUT_MISSING_DEFAULT_BANK ||
    normalizedStatus === VOPAY_ACCOUNT_STATUS.ACTIVE_AND_GRATA_CONFIGURED
  );
}

export function isBuildingVopayMissingPaymentMethod(status) {
  return normalizeVopayAccountStatus(status) === VOPAY_ACCOUNT_STATUS.ACTIVE_BUT_MISSING_PAYMENT_METHOD;
}

/** GET /vopay/buildings/:id/onboarding-status — local fetch so Create Payment modal does not overwrite page Redux. */
export async function fetchBuildingVopayOnboardingStatus(buildingId) {
  const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
  const res = await axios.get(`/vopay/buildings/${buildingId}/onboarding-status`, { headers });
  return res.data;
}

export const VOPAY_ONBOARDING_MODAL_NAMES = ['AccountNotSetupModal', 'OnboardingModal'];

export function isVopayOnboardingModalOpen(modalName) {
  return VOPAY_ONBOARDING_MODAL_NAMES.includes(modalName);
}

export const ONBOARDING_MODAL_MESSAGE = {
  ORG_SETUP: 'org_setup',
  ORG_WAITING: 'org_waiting',
  ORG_BLOCKED: 'org_blocked',
  ORG_CONTACT_SUPPORT: 'org_contact_support',
  BUILDING_WAITING: 'building_waiting',
  BUILDING_BLOCKED: 'building_blocked'
};

/** Rejected / disabled / suspended — no setup link (org or building). */
export function isVopayBlockedAccountStatus(status) {
  const normalizedStatus = normalizeVopayAccountStatus(status);
  return (
    normalizedStatus === VOPAY_ACCOUNT_STATUS.REJECTED ||
    normalizedStatus === VOPAY_ACCOUNT_STATUS.DISABLED ||
    normalizedStatus === VOPAY_ACCOUNT_STATUS.SUSPENDED
  );
}

export function getVopayBlockedStatusLabel(status) {
  const normalizedStatus = normalizeVopayAccountStatus(status);
  if (normalizedStatus === VOPAY_ACCOUNT_STATUS.REJECTED) return 'rejected';
  if (normalizedStatus === VOPAY_ACCOUNT_STATUS.DISABLED) return 'disabled';
  if (normalizedStatus === VOPAY_ACCOUNT_STATUS.SUSPENDED) return 'suspended';
  return normalizedStatus;
}

export function isOnboardingBlockedMessageType(messageType) {
  return (
    messageType === ONBOARDING_MODAL_MESSAGE.ORG_BLOCKED ||
    messageType === ONBOARDING_MODAL_MESSAGE.BUILDING_BLOCKED
  );
}

export const VOPAY_BLOCKED_ORG_ACCOUNT_MESSAGE =
  'Your VoPay account is {{status}}. Please contact support.';

export const VOPAY_BLOCKED_BUILDING_ACCOUNT_MESSAGE =
  "This building's VoPay account is {{status}}. Please contact support.";

export const VOPAY_ORG_APPLICATION_PENDING_CONTACT_SUPPORT_MESSAGE =
  'Your VoPay application is pending. Please contact support.';

export function getVopayBlockedAccountMessageKey(messageType) {
  return messageType === ONBOARDING_MODAL_MESSAGE.BUILDING_BLOCKED
    ? VOPAY_BLOCKED_BUILDING_ACCOUNT_MESSAGE
    : VOPAY_BLOCKED_ORG_ACCOUNT_MESSAGE;
}

export function resolveOrgOnboardingMessageType(status, link) {
  if (isOrgApplicationPendingWithLink(status, link)) {
    return ONBOARDING_MODAL_MESSAGE.ORG_SETUP;
  }
  if (isOrgApplicationPendingWithoutLink(status, link)) {
    return ONBOARDING_MODAL_MESSAGE.ORG_CONTACT_SUPPORT;
  }
  if (isVopayBlockedAccountStatus(status)) {
    return ONBOARDING_MODAL_MESSAGE.ORG_BLOCKED;
  }
  return ONBOARDING_MODAL_MESSAGE.ORG_WAITING;
}

export function resolveBuildingOnboardingMessageType(buildingStatus) {
  if (isVopayBlockedAccountStatus(buildingStatus)) {
    return ONBOARDING_MODAL_MESSAGE.BUILDING_BLOCKED;
  }
  return ONBOARDING_MODAL_MESSAGE.BUILDING_WAITING;
}

/** Opens OnboardingModal with explicit message type (never inferred from building data). */
export function buildOnboardingModalPayload(status, link, messageType) {
  return { status, link, messageType };
}

export function openOrgOnboardingModalFromStatus(status, link, openOnboardingModal) {
  openOnboardingModal(
    buildOnboardingModalPayload(status, link, resolveOrgOnboardingMessageType(status, link))
  );
}

export function getOrgOnboardingGateResult(orgStatus) {
  if (orgStatus == null) {
    return null;
  }
  if (isOrgVopayActive(orgStatus)) {
    return 'active';
  }
  return 'blocked';
}

function openOrgOnboardingModal(status, link, { openOnboardingModal }) {
  openOrgOnboardingModalFromStatus(status, link, openOnboardingModal);
}

export function handleOrganizationOnboardingStatusResponse(status, link, { openOnboardingModal }) {
  if (isOrgVopayActive(status)) {
    return;
  }
  openOrgOnboardingModal(status, link, { openOnboardingModal });
}

export function handleBuildingOnboardingStatusResponse(
  buildingStatus,
  buildingLink,
  buildingId,
  { openOnboardingModal, openBuildingBankSetup, loadPayments, orgStatus, orgLink }
) {
  if (orgStatus == null) {
    return;
  }
  if (!isOrgVopayActive(orgStatus)) {
    openOrgOnboardingModal(orgStatus, orgLink, { openOnboardingModal });
    return;
  }

  if (isBuildingVopayReadyForPayments(buildingStatus)) {
    loadPayments(buildingId);
    return;
  }
  if (isBuildingVopayMissingPaymentMethod(buildingStatus)) {
    openBuildingBankSetup(buildingId);
    return;
  }
  openOnboardingModal(
    buildOnboardingModalPayload(
      buildingStatus,
      buildingLink,
      resolveBuildingOnboardingMessageType(buildingStatus)
    )
  );
}

export function formatPaymentFrequency(value) {
  const s = String(value || '').trim();
  if (!s) return '—';
  return s
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function paymentRowMatchesSearch(row, qLower) {
  if (!qLower) return true;
  const amtRaw = String(row.amount ?? '').toLowerCase();
  const amtFmt = formatAmountTwoDecimals(row.amount).toLowerCase();
  return (
    (row.residentName || '').toLowerCase().includes(qLower) ||
    (row.unitNum || '').toLowerCase().includes(qLower) ||
    (row.status || '').toLowerCase().includes(qLower) ||
    amtRaw.includes(qLower) ||
    (amtFmt && amtFmt.includes(qLower)) ||
    (row.purpose || '').toLowerCase().includes(qLower)
  );
}

/** Scheduled row: cancel API only when status is in progress or processing (not upcoming/pending). */
export function paymentRowMatchesStatusFilter(row, statusFilter) {
  if (!statusFilter || statusFilter === 'all') return true;
  const s = normalizePaymentStatusKey(row.status);
  switch (statusFilter) {
    case 'in_progress':
      return (
        s === 'pending' ||
        s === 'processing' ||
        s === 'in_progress' ||
        s === 'initiated' ||
        s === 'upcoming'
      );
    case 'failed':
      return s === 'failed' || s === 'error';
    case 'successful':
      return s === 'complete' || s === 'successful' || s === 'completed';
    case 'cancelled':
      return s === 'cancelled';
    default:
      return true;
  }
}

/** month: 1–12 calendar month; bounds are local start/end of month converted to UTC Zulu for the API. */
export function getMonthUtcRangeIso(month, year) {
  const startLocal = moment({ year, month: month - 1, day: 1 }).startOf('day');
  const endLocal = startLocal.clone().endOf('month').millisecond(0);

  return {
    startDate: startLocal.utc().format(PAYMENTS_RFC3339_ZULU_FORMAT),
    endDate: endLocal.utc().format(PAYMENTS_RFC3339_ZULU_FORMAT)
  };
}

/** Local calendar day range (inclusive) → UTC Zulu RFC3339 bounds for payment history APIs. */
export function getLocalDayRangeUtcIso(startMoment, endMoment) {
  const startLocal = moment(startMoment).startOf('day');
  const endLocal = moment(endMoment).endOf('day').millisecond(0);
  return {
    startDate: startLocal.utc().format(PAYMENTS_RFC3339_ZULU_FORMAT),
    endDate: endLocal.utc().format(PAYMENTS_RFC3339_ZULU_FORMAT)
  };
}

export async function fetchBuildingPaymentsCsvExport(buildingId, startDate, endDate) {
  const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
  const res = await axios.get(`/vopay/buildings/${buildingId}/payments/csv/selected-dates`, {
    params: { start_date: startDate, end_date: endDate },
    headers
  });
  return Array.isArray(res.data) ? res.data : [];
}

/** Display payment / scheduled-payment id in the payments table. */
export function formatPaymentTableId(id) {
  if (id == null || String(id).trim() === '') {
    return '—';
  }
  return String(id);
}

/** Format API createdAt (RFC3339) in the offset from the response — no browser local conversion. */
export function formatPaymentCreatedAtFromApi(createdAt, format = 'YYYY-MM-DD') {
  if (createdAt == null || createdAt === '') return '';
  const m = moment.parseZone(String(createdAt).trim());
  return m.isValid() ? m.format(format) : '';
}

/** Map VoPay payment history rows to react-csv row objects (PAYMENTS_CSV_HEADERS keys). */
export function mapPaymentHistoryRowsToCsvData(rows, buildingNameFallback = '') {
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const paymentId = row.paymentId ?? '';
    const paymentDate = formatPaymentCreatedAtFromApi(row.createdAt, 'YYYY-MM-DD');
    return {
      paymentId: paymentId != null ? String(paymentId) : '',
      buildingName: row.buildingName ?? buildingNameFallback,
      unitNum: row.unitNum != null && row.unitNum !== '' ? String(row.unitNum) : '',
      tenantName: row.residentName ?? '',
      amount: row.amount != null ? formatAmountTwoDecimals(row.amount) || String(row.amount) : '',
      paymentMethod: formatPaymentMethodForCsv(row.paymentMethodType, row.paymentMethodLast4Digits),
      paymentStatus: row.status ?? '',
      hasRefunded: row.hasRefunded === true ? 'true' : row.hasRefunded === false ? 'false' : '',
      purpose: row.Purpose ?? row.purpose ?? 'Payment',
      period: row.paymentPeriod ?? '',
      paymentDate
    };
  });
}

const PAYMENTS_CSV_FILENAME_MONTH_FORMAT = 'MMM YYYY';
const PAYMENTS_CSV_FILENAME_RANGE_FORMAT = 'MMM DD, YYYY';

/** Whole-month export filename, e.g. "Payments Jan 2026.csv" (local time). */
export function buildWholeMonthPaymentsCsvFilename(selectedMonthDate) {
  const month = moment(selectedMonthDate);
  const label = month.isValid() ? month.format(PAYMENTS_CSV_FILENAME_MONTH_FORMAT) : moment().format(PAYMENTS_CSV_FILENAME_MONTH_FORMAT);
  return `Payments ${label}.csv`;
}

/** Selected date range export filename, e.g. "Payments Jan 01, 2026 - Mar 15, 2026.csv" (local time). */
export function buildDateRangePaymentsCsvFilename(startDate, endDate) {
  const startLabel = moment(startDate).format(PAYMENTS_CSV_FILENAME_RANGE_FORMAT);
  const endLabel = moment(endDate).format(PAYMENTS_CSV_FILENAME_RANGE_FORMAT);
  return `Payments ${startLabel} - ${endLabel}.csv`;
}

/**
 * VoPay payment export: human-readable payment method, e.g. "Bank end with 1234".
 * Maps `bank_account` to "Bank"; other types are shown as returned (trimmed).
 */
export function formatPaymentMethodForCsv(paymentMethodType, paymentMethodLast4Digits) {
  const rawType = String(paymentMethodType || '').trim();
  const displayType =
    rawType.toLowerCase() === 'bank_account' ? 'Bank' : rawType;
  const last4 = String(paymentMethodLast4Digits || '').trim();
  if (!displayType && !last4) return '';
  if (!last4) return displayType;
  if (!displayType) return `(${last4})`;
  return `${displayType} (${last4})`;
}

export async function fetchBuildingVopayHistories(buildingId, month, year) {
  const { startDate, endDate } = getMonthUtcRangeIso(month, year);
  const params = { start_date: startDate, end_date: endDate };
  const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
  const [paymentsRes, scheduledRes] = await Promise.all([
    axios.get(`/vopay/buildings/${buildingId}/payments`, { params, headers }),
    axios.get(`/vopay/buildings/${buildingId}/scheduled-payments`, { params, headers })
  ]);
  const oneTime = Array.isArray(paymentsRes.data) ? paymentsRes.data : [];
  const scheduled = Array.isArray(scheduledRes.data) ? scheduledRes.data : [];
  return { oneTime, scheduled };
}

export async function fetchBuildingOneTimePaymentDetail(buildingId, unitId, paymentId) {
  const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
  const res = await axios.get(
    `/vopay/buildings/${buildingId}/units/${unitId}/payments/${paymentId}`,
    { headers }
  );
  return res.data;
}

function parsePositiveInt(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function buildRentReceiptRequestBody(data) {
  const paymentId = parsePositiveInt(data?.payment_id ?? data?.paymentId);
  const userId = parsePositiveInt(data?.user_id ?? data?.userId ?? data?.payer_id ?? data?.payerId);
  const buildingId = parsePositiveInt(data?.building_id ?? data?.buildingId);
  if (!paymentId || !userId || !buildingId) return null;
  return { paymentId, userId, buildingId };
}

export async function fetchVopayReceiptPreview(body) {
  const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
  const res = await axios.post('/vopay/receipt/preview', body, { headers });
  return res.data;
}

function extractFilenameFromContentDisposition(headerValue, fallbackName) {
  const fallback = fallbackName || 'receipt.pdf';
  if (!headerValue) return fallback;
  const headerStr =
    typeof headerValue === 'string' ? headerValue : Array.isArray(headerValue) ? headerValue[0] : String(headerValue);
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(headerStr);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch (_e) {
      return utf8Match[1];
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(headerStr);
  if (quoted?.[1]) return quoted[1];
  const plain = /filename=([^;]+)/i.exec(headerStr);
  return plain?.[1]?.trim() || fallback;
}

function pickResponseHeader(res, ...names) {
  if (!res?.headers) return null;
  const h = res.headers;
  if (typeof h.get === 'function') {
    for (const name of names) {
      const v = h.get(name);
      if (v != null && String(v).trim() !== '') return v;
    }
    return null;
  }
  const byLower = {};
  for (const key of Object.keys(h)) {
    byLower[key.toLowerCase()] = h[key];
  }
  for (const name of names) {
    const v = byLower[name.toLowerCase()];
    if (v != null && String(v).trim() !== '') return v;
  }
  return null;
}

function normalizeResponseToBlob(data) {
  if (data instanceof Blob) return data;
  if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
    return new Blob([data], { type: 'application/pdf' });
  }
  if (data?.buffer instanceof ArrayBuffer && typeof data.byteLength === 'number') {
    return new Blob([data], { type: 'application/pdf' });
  }
  if (typeof data === 'string') return new Blob([data], { type: 'application/pdf' });
  return null;
}

export async function axiosErrorMessageForToast(error, unknownMessage) {
  const fallback = unknownMessage != null ? String(unknownMessage) : 'Unknown error';
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const j = JSON.parse(text);
        return j.message || j.Message || fallback;
      } catch (_parse) {
        return text ? String(text).slice(0, 240) : fallback;
      }
    } catch (_read) {
      return fallback;
    }
  }
  if (data && typeof data === 'object' && (data.message || data.Message)) {
    return String(data.message || data.Message);
  }
  return error?.message || fallback;
}

export async function downloadVopayReceiptPdf(body) {
  if (!body || body.paymentId == null) {
    throw new Error('Missing receipt request');
  }
  const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  const headers = {
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    Accept: 'application/pdf,application/octet-stream;q=0.9,*/*;q=0.8'
  };
  const res = await axios.post('/vopay/receipt/pdf', body, {
    headers,
    responseType: 'blob'
  });
  if (typeof window === 'undefined') return;

  if (!res || res.data == null) {
    throw new Error('Empty response from server');
  }

  const blob = normalizeResponseToBlob(res.data);
  if (!blob) {
    throw new Error('Invalid PDF payload');
  }

  const contentType = `${pickResponseHeader(res, 'content-type', 'Content-Type') || ''} ${blob.type || ''}`.toLowerCase();
  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    const text = await blob.text();
    let msg = 'Failed to download receipt';
    try {
      const j = JSON.parse(text);
      msg = j.message || j.Message || msg;
    } catch (_e) {
      if (text) msg = String(text).slice(0, 240);
    }
    throw new Error(msg);
  }

  const defaultFile = `Receipt ${body.paymentId}.pdf`;
  const filename = extractFilenameFromContentDisposition(
    pickResponseHeader(res, 'content-disposition', 'Content-Disposition'),
    defaultFile
  );

  let objectUrl;
  try {
    objectUrl = window.URL.createObjectURL(blob);
    const anchor = window.document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    window.document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    if (objectUrl) {
      window.setTimeout(() => {
        try {
          window.URL.revokeObjectURL(objectUrl);
        } catch {
          /* revoke is best-effort */
        }
      }, 250);
    }
  }
}

export function mapOneTimePaymentDetailToModalPatch(detail) {
  if (!detail || typeof detail !== 'object') return { one_time_detail_loaded: true };
  const last4 = detail.paymentMethodLast4 ?? detail.paymentMethodLast4Digits ?? '';
  const amt = parseHistoryAmount(detail.amount);
  const addr = detail.buildingAddress != null ? String(detail.buildingAddress).trim() : '';
  const out = {
    receipt_number: detail.receiptNumber != null ? String(detail.receiptNumber) : '',
    funding_status: detail.fundingStatus != null ? String(detail.fundingStatus) : '',
    transfer_status: detail.transferStatus != null ? String(detail.transferStatus) : '',
    withdraw_status: detail.withdrawStatus != null ? String(detail.withdrawStatus) : '',
    refund_status: detail.refundStatus != null ? String(detail.refundStatus) : '',
    payment_purpose: detail.paymentPurpose != null ? String(detail.paymentPurpose) : '',
    updated_at: detail.updatedAt,
    created_at: detail.createdAt,
    settlement_status: detail.paymentStatus != null ? String(detail.paymentStatus) : '',
    amount: amt,
    paymentMethodType: detail.paymentMethodType != null ? String(detail.paymentMethodType) : '',
    paymentMethodLast4Digits: last4 != null ? String(last4) : '',
    unit_num: detail.unitNum != null ? String(detail.unitNum) : '',
    building_address: addr,
    one_time_detail_loaded: true
  };
  if (detail.hasRefunded === true) {
    out.has_refunded = true;
    if (detail.refundId != null) {
      out.refund_id = String(detail.refundId);
    }
  }
  if (detail.canRefund === true) {
    out.can_refund = true;
  }
  if (detail.depositAmount != null && String(detail.depositAmount).trim() !== '') {
    out.deposit_amount = parseHistoryAmount(detail.depositAmount);
  }
  if (detail.isRefund === true) {
    out.is_refund = true;
  }
  const inst = detail.institutionNumber != null ? String(detail.institutionNumber).trim() : '';
  if (inst) {
    out.institution_number = inst;
  }
  const bankName = detail.bankName != null ? String(detail.bankName).trim() : '';
  if (bankName) {
    out.bankName = bankName;
  }
  const payerName = detail.payerName != null ? String(detail.payerName).trim() : '';
  if (payerName) {
    out.payer_name = payerName;
  }
  if (Array.isArray(detail.tenants) && detail.tenants.length) {
    out.tenants = detail.tenants.filter(Boolean);
  }
  return out;
}

/** Same normalization as Payments page status filter (e.g. "in progress" → in_progress). */
export function normalizePaymentStatusKey(status) {
  return String(status || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

/**
 * VoPay funding / transfer / withdraw sub-status → UI tier for stage pills (icons + colors).
 */
export function classifyVopayTransactionStageTier(status) {
  const raw = status == null ? '' : String(status).trim();
  if (!raw) return 'pending';
  const s = normalizePaymentStatusKey(raw);
  if (s === 'complete' || s === 'successful' || s === 'completed' || s === 'settled') return 'complete';
  if (s === 'failed' || s === 'error') return 'failed';
  if (s === 'cancelled') return 'cancelled';
  if (
    s === 'pending' ||
    s === 'processing' ||
    s === 'in_progress' ||
    s === 'initiated' ||
    s === 'upcoming'
  ) {
    return 'in_progress';
  }
  return 'unknown';
}

export function paymentListStatusBadgeMeta(status) {
  if (!String(status || '').trim()) {
    return null;
  }
  const s = normalizePaymentStatusKey(status);
  if (s === 'pending' || s === 'processing' || s === 'in_progress' || s === 'initiated') {
    return { label: 'In Progress', variant: 'inProgress' };
  }
  if (s === 'complete' || s === 'successful' || s === 'completed' || s === 'settled') {
    return { label: 'Successful', variant: 'complete' };
  }
  if (s === 'failed' || s === 'error') {
    return { label: 'Failed', variant: 'danger' };
  }
  if (s === 'cancelled') {
    return { label: 'Cancelled', variant: 'archived' };
  }
  if (s === 'upcoming') {
    return { label: 'Upcoming', variant: 'inProgress' };
  }
  return null;
}

export function capitalizePaymentStatusWords(status) {
  const s = String(status || '').trim();
  if (!s) return '—';
  const parts = s
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  if (!parts.length) return '—';
  return parts.join(' ');
}

/**
 * Successful / In Progress buckets match `paymentRowMatchesStatusFilter` in Payments.jsx
 * for those filter keys (not raw API strings).
 */
function oneTimeAmountInSuccessfulBucket(status) {
  const s = normalizePaymentStatusKey(status);
  return s === 'complete' || s === 'successful' || s === 'completed';
}

function oneTimeAmountInInProgressBucket(status) {
  const s = normalizePaymentStatusKey(status);
  return (
    s === 'pending' ||
    s === 'processing' ||
    s === 'in_progress' ||
    s === 'initiated' ||
    s === 'upcoming'
  );
}

function oneTimeAmountInFailedBucket(status) {
  const s = normalizePaymentStatusKey(status);
  return s === 'failed' || s === 'error';
}

function isRefundPaymentRow(row) {
  return row?.isRefund === true || row?.paymentKind === 'refund';
}

/**
 * Dashboard stats for PaymentWidgets. Uses one-time (regular) payments only; scheduled rows ignored.
 * payments = sum of inbound amounts; collected = successful inbound − successful refunds;
 * in_progress = in-progress inbound only; failed = failed inbound only.
 */
export function aggregateBuildingPaymentStatsFromHistory(regularPayments, _scheduledPayments) {
  const regular = Array.isArray(regularPayments) ? regularPayments : [];

  const amountOf = (row) => {
    const v = row?.amount;
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    const n = parseFloat(String(v ?? 0), 10);
    return Number.isNaN(n) ? 0 : n;
  };

  const inbound = regular.filter((p) => !isRefundPaymentRow(p));
  const refunds = regular.filter(isRefundPaymentRow);

  const payments = inbound.reduce((s, p) => s + amountOf(p), 0);

  let successfulInbound = 0;
  let successfulRefunds = 0;
  let inProgress = 0;
  let failed = 0;

  for (const p of inbound) {
    const amt = amountOf(p);
    if (oneTimeAmountInSuccessfulBucket(p.status)) successfulInbound += amt;
    else if (oneTimeAmountInInProgressBucket(p.status)) inProgress += amt;
    else if (oneTimeAmountInFailedBucket(p.status)) failed += amt;
  }

  for (const p of refunds) {
    const amt = amountOf(p);
    if (oneTimeAmountInSuccessfulBucket(p.status)) successfulRefunds += amt;
  }

  return {
    payments,
    payments_collected: successfulInbound - successfulRefunds,
    in_progress: inProgress,
    failed
  };
}

function compareValues(a, b, dir) {
  const m = dir === 'DESC' ? -1 : 1;
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * m;
  return String(a).localeCompare(String(b), undefined, { numeric: true }) * m;
}

export function sortPaymentHistoryRows(rows, sortKey, direction, kind) {
  if (!sortKey || !direction || !Array.isArray(rows)) return rows;
  const dir = direction === 'DESC' ? 'DESC' : 'ASC';
  const getVal = (row) => {
    if (kind === 'scheduled') {
      switch (sortKey) {
        case 'id':
          return parseInt(String(row.scheduledPaymentId ?? ''), 10) || 0;
        case 'resident':
          return row.residentName;
        case 'unit':
          return row.unitNum || '';
        case 'startDate':
          return row.scheduleStartDate ? new Date(row.scheduleStartDate).getTime() : 0;
        case 'endDate':
          return row.scheduleEndDate ? new Date(row.scheduleEndDate).getTime() : 0;
        case 'frequency':
          return row.frequency || '';
        case 'amount':
          return parseFloat(String(row.amount ?? 0), 10) || 0;
        case 'status':
          return row.status || '';
        default:
          return '';
      }
    }
    switch (sortKey) {
      case 'id':
        return parseInt(String(row.paymentId ?? ''), 10) || 0;
      case 'resident':
        return row.residentName;
      case 'unit':
        return row.unitNum || '';
      case 'date':
        return row.createdAt ? new Date(row.createdAt).getTime() : 0;
      case 'amount':
        return parseFloat(String(row.amount ?? 0), 10) || 0;
      case 'status':
        return row.status || '';
      default:
        return '';
    }
  };
  return [...rows].sort((x, y) => compareValues(getVal(x), getVal(y), dir));
}

/** Two fractional digits for display / forms (e.g. 10 → "10.00"). Empty when missing or non-numeric. */
export function formatAmountTwoDecimals(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toFixed(2);
}

/** `$` + two decimals, or em dash when value is not a finite number. */
export function formatUsdTwoDecimals(value) {
  const s = formatAmountTwoDecimals(value);
  return s === '' ? '—' : `$${s}`;
}

/** Table amount display; refund rows are shown with a leading `-` (e.g. `-$10.00`). */
export function formatPaymentTableAmount(row) {
  const formatted = formatUsdTwoDecimals(row?.amount);
  if (formatted === '—' || !isRefundPaymentRow(row)) return formatted;
  return `-${formatted}`;
}

/** Format an API UTC timestamp for display in the browser's local timezone. */
export function formatUtcTimestampLocal(value, format = 'MMM DD, YYYY') {
  if (value == null || value === '') return '';
  const parsed = moment.utc(value).local();
  return parsed.isValid() ? parsed.format(format) : '';
}

/**
 * Matches api-admin GrataFundTransferWithdrawRequest (POST /vopay/payments).
 * Resident pays building: payer=user, payee=building.
 */
export function buildGrataFundTransferWithdrawBody({
  buildingId,
  userId,
  amount,
  notes,
  paymentDescriptor,
  paymentCategoryId = null
}) {
  const ik =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return {
    BuildingID: buildingId,
    PayerID: userId,
    PayerType: 'user',
    PayeeID: buildingId,
    PayeeType: 'building',
    PaymentCategoryID: paymentCategoryId,
    PaymentDescriptor: String(paymentDescriptor ?? '').trim().slice(0, 7),
    Amount: Number(amount).toFixed(2),
    Notes: String(notes ?? '').trim(),
    IK: ik
  };
}

function toScheduleDateRfc3339UtcMidnight(date) {
  return moment.utc(date).startOf('day').format('YYYY-MM-DD[T]HH:mm:ss[Z]');
}

/**
 * Matches api-admin GrataFundTransferWithdrawScheduleRequest (POST /vopay/scheduled-payments).
 */
export function buildGrataFundTransferScheduleBody({
  buildingId,
  userId,
  amount,
  notes,
  frequency,
  startDate,
  endDate,
  paymentCategoryId = null
}) {
  const ik =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return {
    BuildingID: buildingId,
    PayerID: userId,
    PayerType: 'user',
    PayeeID: buildingId,
    PayeeType: 'building',
    PaymentCategoryID: paymentCategoryId,
    Amount: Number(amount).toFixed(2),
    Notes: notes ?? '',
    IK: ik,
    Frequency: frequency,
    ScheduleStartDate: toScheduleDateRfc3339UtcMidnight(startDate),
    ScheduleEndDate: toScheduleDateRfc3339UtcMidnight(endDate)
  };
}

function parseHistoryAmount(v) {
  if (v == null) return 0;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const n = parseFloat(String(v), 10);
  return Number.isNaN(n) ? 0 : n;
}


/** Receipt modal / PDF-style: "Bank - **** 1234" (last 4 only). */
export function formatReceiptPaymentMethodDisplay(paymentMethodType, paymentMethodLast4) {
  const last4 = String(paymentMethodLast4 ?? '').trim();
  const raw = String(paymentMethodType ?? '')
    .trim()
    .toLowerCase();
  if (!last4) return '—';
  let label = 'Bank';
  if (raw === 'bank_account') {
    label = 'Bank';
  } else if (raw) {
    label = raw
      .split('_')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return `${label} - **** ${last4}`;
}

export function formatBuildingPaymentMethodDisplay(paymentMethodType, paymentMethodLast4Digits) {
  const last4 = String(paymentMethodLast4Digits ?? '').trim();
  const raw = String(paymentMethodType ?? '')
    .trim()
    .toLowerCase();
  if (!last4 || !raw) {
    return '—';
  }
  let label;
  if (raw === 'bank_account') {
    label = 'Bank';
  } else {
    label = raw
      .split('_')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  if (!label) {
    return '—';
  }
  return `${label} *** ***** *${last4}`;
}

/** Pad institution / metadata keys to 3 characters for GET /bank-logos map lookups (e.g. "6" → "006"). */
export function padBankLogoMetadataKey(key) {
  const s = String(key ?? '').trim();
  if (!s) return '';
  if (s.length >= 3) return s;
  return s.padStart(3, '0');
}

/** Re-key bank logo API payload so every entry uses a 3-digit institution key. */
export function normalizeBankLogosMap(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    if (v && typeof v === 'object') {
      out[padBankLogoMetadataKey(k)] = v;
    }
  }
  return out;
}

let bankLogosCache = null;
let bankLogosInflight = null;
const BUILDING_PAYMENT_PURPOSES_STORAGE_PREFIX = 'building-payment-purposes';

function buildingPaymentPurposesStorageKey(buildingId) {
  return `${BUILDING_PAYMENT_PURPOSES_STORAGE_PREFIX}:${buildingId}`;
}

/** @type {Map<number, Array<{ id: number, label: string, descriptor: string, rowType?: string }>>} */
const buildingPaymentPurposesCacheByBuilding = new Map();
/** @type {Map<number, Promise<Array<{ id: number, label: string, descriptor: string, rowType?: string }>>>} */
const buildingPaymentPurposesInflightByBuilding = new Map();

/**
 * Fetches GET /bank-logos once and caches normalized keys (3-digit institution numbers).
 * Returns the cached map, or {} on failure (does not cache failures so the next call can retry).
 */
export async function ensureBankLogosCached() {
  if (bankLogosCache != null) return bankLogosCache;
  if (!bankLogosInflight) {
    const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
    const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
    bankLogosInflight = axios
      .get('/bank-logos', { headers })
      .then((res) => {
        bankLogosCache = normalizeBankLogosMap(res.data || {});
        return bankLogosCache;
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('ensureBankLogosCached', err);
        return {};
      })
      .finally(() => {
        bankLogosInflight = null;
      });
  }
  return bankLogosInflight;
}

function normalizeRentPaymentPurposes(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = {
        id: Number(item.id),
        label: String(item.label ?? '').trim(),
        descriptor: String(item.descriptor ?? '').trim()
      };
      if (item.rowType != null && String(item.rowType).trim() !== '') {
        row.rowType = String(item.rowType).trim();
      }
      return row;
    })
    .filter((item) => Number.isFinite(item?.id) && item.id > 0 && item.label);
}

function readBuildingPaymentPurposesFromLocalStorage(buildingId) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(buildingPaymentPurposesStorageKey(buildingId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const normalized = normalizeRentPaymentPurposes(parsed);
    return normalized.length ? normalized : null;
  } catch (_e) {
    return null;
  }
}

function writeBuildingPaymentPurposesToLocalStorage(buildingId, value) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(buildingPaymentPurposesStorageKey(buildingId), JSON.stringify(value));
  } catch (_e) {
    // best-effort cache write
  }
}

/**
 * Uses localStorage first, then GET /vopay/buildings/:id/payment-purposes as fallback.
 * Caches in memory + localStorage per building, and coalesces parallel requests.
 * @param {number} buildingId
 */
export async function ensureRentPaymentPurposesCached(buildingId) {
  const bid = Number(buildingId);
  if (!Number.isFinite(bid) || bid <= 0) return [];

  if (buildingPaymentPurposesCacheByBuilding.has(bid)) {
    return buildingPaymentPurposesCacheByBuilding.get(bid);
  }

  const fromStorage = readBuildingPaymentPurposesFromLocalStorage(bid);
  if (fromStorage) {
    buildingPaymentPurposesCacheByBuilding.set(bid, fromStorage);
    return fromStorage;
  }

  if (!buildingPaymentPurposesInflightByBuilding.has(bid)) {
    const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
    const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
    const req = axios
      .get(`/vopay/buildings/${bid}/payment-purposes`, { headers })
      .then((res) => {
        const normalized = normalizeRentPaymentPurposes(res.data || []);
        buildingPaymentPurposesCacheByBuilding.set(bid, normalized);
        writeBuildingPaymentPurposesToLocalStorage(bid, normalized);
        return normalized;
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('ensureRentPaymentPurposesCached', err);
        return [];
      })
      .finally(() => {
        buildingPaymentPurposesInflightByBuilding.delete(bid);
      });
    buildingPaymentPurposesInflightByBuilding.set(bid, req);
  }

  return buildingPaymentPurposesInflightByBuilding.get(bid);
}

/**
 * POST a building-specific payment purpose; replaces in-memory cache and building-payment-purposes localStorage with the API response (full list).
 * @param {number} buildingId
 * @param {string} label Purpose label
 * @param {string} descriptor Short VoPay descriptor (required, max 7 chars)
 * @returns {Promise<{ list: Array<{ id: number, label: string, descriptor: string, rowType?: string }> | null, error?: string }>}
 */
export async function createBuildingPaymentPurposeAndRefreshCache(buildingId, label, descriptor) {
  const bid = Number(buildingId);
  const trimmedLabel = String(label ?? '').trim();
  const trimmedDescriptor = String(descriptor ?? '').trim().slice(0, 7);
  if (!Number.isFinite(bid) || bid <= 0 || !trimmedLabel) {
    return { list: null, error: 'Enter a purpose name' };
  }
  if (!trimmedDescriptor) {
    return { list: null, error: 'Descriptor is required' };
  }
  const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
  try {
    const res = await axios.post(
      `/vopay/buildings/${bid}/payment-purposes`,
      { label: trimmedLabel, descriptor: trimmedDescriptor },
      { headers }
    );
    const normalized = normalizeRentPaymentPurposes(res.data || []);
    buildingPaymentPurposesCacheByBuilding.set(bid, normalized);
    writeBuildingPaymentPurposesToLocalStorage(bid, normalized);
    return { list: normalized };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('createBuildingPaymentPurposeAndRefreshCache', err);
    const data = err?.response?.data;
    const msg =
      (typeof data === 'string' && data) ||
      data?.message ||
      data?.error ||
      err?.message ||
      'Failed to add purpose';
    return { list: null, error: String(msg) };
  }
}

/** Payment detail modal: refund enabled when detail API marks refundable. */
export function paymentCanRefund(data) {
  return data?.can_refund === true && data?.is_refund !== true && data?.has_refunded !== true;
}

/** Same rules as Payments.jsx scheduled row cancel (VoPay allows cancel for in-progress / processing). */
export function scheduledTransferCanCancel(row) {
  const sid = row?.scheduledTransfersId;
  if (sid == null || String(sid).trim() === '') return false;
  const s = normalizePaymentStatusKey(row.status);
  return s === 'in_progress' || s === 'processing' || s === 'initiated';
}

/** Map VoPay history row to PaymentDetailsSideModal / cancel / receipt modal shape */
export function buildPaymentDetailsModalPayload(row, kind, buildingId, buildingName) {
  const amt = parseHistoryAmount(row.amount);
  const base = {
    user_name: row.residentName,
    building_name: row.buildingName || buildingName || '',
    currency: 'CAD',
    amount: amt,
    user_id: row.residentId != null ? Number(row.residentId) : undefined,
    paymentMethodLast4Digits: row.paymentMethodLast4Digits ?? row.last4Digits ?? '',
    paymentMethodType: row.paymentMethodType ?? '',
    notes: row.notes,
    bankName: row.bankName != null ? String(row.bankName).trim() : '',
    bankNameShort: row.bankNameShort != null ? String(row.bankNameShort).trim() : ''
  };
  if (kind === 'scheduled') {
    const sid = row.scheduledTransfersId;
    const tenants = Array.isArray(row.tenants) ? row.tenants.filter(Boolean) : [];
    return {
      ...base,
      payment_type: VOPAY_PAYMENT_TYPE.SCHEDULED,
      frequency: row.frequency,
      schedule_start_date: row.scheduleStartDate,
      schedule_end_date: row.scheduleEndDate,
      schedule_status: row.status,
      vopay_scheduled_transfers_id: sid != null ? String(sid) : '',
      building_id: Number(buildingId),
      created_at: row.createdAt,
      updated_at: row.updatedAt,
      scheduled_payment_id: sid,
      tenants,
      building_address: row.buildingAddress ?? '',
      unit_num: row.unitNum ?? '',
      institution_number:
        row.institutionNumber != null && row.institutionNumber !== ''
          ? String(row.institutionNumber).trim()
          : '',
      can_cancel_recurring: scheduledTransferCanCancel(row)
    };
  }
  const paymentIdStr =
    row.paymentId != null && String(row.paymentId).trim() !== '' ? String(row.paymentId).trim() : '';
  return {
    ...base,
    payment_type: VOPAY_PAYMENT_TYPE.ONE_TIME,
    created_at: row.createdAt,
    updated_at: row.updatedAt,
    settlement_status: row.status,
    building_id: Number(buildingId),
    unit_id: row.unitId != null ? Number(row.unitId) : 0,
    payment_id: paymentIdStr,
    payment_purpose: row.purpose != null && String(row.purpose).trim() !== ''
      ? String(row.purpose)
      : row.Purpose != null && String(row.Purpose).trim() !== ''
        ? String(row.Purpose)
        : 'Payment',
    institution_number:
      row.institutionNumber != null && row.institutionNumber !== ''
        ? String(row.institutionNumber).trim()
        : '',
    has_refunded: row.hasRefunded === true,
    refund_id: row.refundId != null ? String(row.refundId) : '',
    one_time_detail_loaded: false
  };
}
